import env from 'env';
import L, { FitBoundsOptions } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  FeatureGroup,
  MapContainer,
  MapContainerProps,
  TileLayer,
  useMap,
} from 'react-leaflet';
import MapInteraction, {
  MapInteractionProps,
} from './map-interaction/map-interaction';
import './map.css';

export const MAP_RENDERER_PADDING = 1;

type MapProps = {
  height?: string;
  children?: ReactNode;
  fit?: boolean;
  // Delays the fit until the children are loaded, e.g. fetched geometries
  fitReady?: boolean;
} & MapContainerProps;

interface FitBoundsProps {
  children?: ReactNode;
  ready: boolean;
}

const FitBounds: FunctionComponent<FitBoundsProps> = ({ children, ready }) => {
  const map = useMap();
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  // Fitted once only: later changes (e.g. selected cities) keep the view
  const fittedBoundsRef = useRef<L.LatLngBounds>();
  const boundOptions: FitBoundsOptions = { maxZoom: 12 };

  useEffect(() => {
    if (!ready || fittedBoundsRef.current || !featureGroupRef.current) return;
    const bounds = featureGroupRef.current.getBounds();
    if (!bounds.isValid()) return;

    fittedBoundsRef.current = bounds;
    map.fitBounds(bounds, boundOptions);
  }, [map, ready]);

  useEffect(() => {
    // Not animated, so that the view can be set again right after the fit
    const onResize = () => {
      if (!fittedBoundsRef.current) return;
      map.fitBounds(fittedBoundsRef.current, {
        ...boundOptions,
        animate: false,
      });
    };

    map.addEventListener('resize', onResize);

    return () => {
      map.removeEventListener('resize', onResize);
    };
  }, [map]);

  return <FeatureGroup ref={featureGroupRef}>{children}</FeatureGroup>;
};

const HoverTooltips: FunctionComponent = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    const closeTooltips = (kept?: L.Tooltip) => {
      map.eachLayer((layer) => {
        if (
          layer instanceof L.Tooltip &&
          layer !== kept &&
          !layer.options.permanent
        ) {
          layer.close();
        }
      });
    };

    const onTooltipOpen = (event: L.TooltipEvent) =>
      closeTooltips(event.tooltip);
    const onMouseLeave = () => closeTooltips();

    map.on('tooltipopen', onTooltipOpen);
    container.addEventListener('mouseleave', onMouseLeave);

    return () => {
      map.off('tooltipopen', onTooltipOpen);
      container.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [map]);

  return null;
};

const Map: FunctionComponent<MapProps & MapInteractionProps> = ({
  children,
  height,
  zoom = 6,
  center = [46.71, 3],
  fit = true,
  fitReady = true,
  ...rest
}) => {
  const mapInteractionProps: MapInteractionProps = rest;

  const mapProps: MapProps = rest;

  const renderer = useMemo(() => L.svg({ padding: MAP_RENDERER_PADDING }), []);

  return (
    <MapContainer
      style={{ height: height ?? '100vh' }}
      zoom={zoom}
      center={center}
      maxZoom={15}
      minZoom={6}
      zoomSnap={0.5}
      scrollWheelZoom={false}
      attributionControl={false}
      renderer={renderer}
      {...mapProps}
    >
      <TileLayer url={`${env.MAP_URL}/styles/bilik/{z}/{x}/{y}.png`} />
      {fit ? <FitBounds ready={fitReady}>{children}</FitBounds> : children}
      <HoverTooltips />
      <MapInteraction {...mapInteractionProps} />
    </MapContainer>
  );
};

export default Map;
